import { STATUS_FILTER_OPTIONS } from "../constants/paymentsActivity.constants";
import type { ActivityDirection, ActivityScope } from "../types/paymentsActivity.types";

/** Route base of the activity view. Kept here so every module linking in agrees on one path and param names. */
export const PAYMENTS_ACTIVITY_BASE_PATH = "/app/payments-activity";

/** Query param that carries the open operation. Its presence IS the drawer's open state. */
export const ACTIVITY_OPERATION_PARAM = "operation";
/** Query param that says which endpoint the id belongs to — an id alone cannot tell payin from payout. */
export const ACTIVITY_DIRECTION_PARAM = "direction";

/**
 * Deep link that opens the activity view with one operation's drawer already open.
 *
 * `direction` is not decoration: payins and payouts are two distinct endpoints with two distinct status
 * vocabularies, and nothing in a uuid says which one it is.
 */
export function buildActivityOperationPath(direction: ActivityDirection, operationId: string): string {
  const query = new URLSearchParams({
    [ACTIVITY_DIRECTION_PARAM]: direction,
    [ACTIVITY_OPERATION_PARAM]: operationId,
  });
  return `${PAYMENTS_ACTIVITY_BASE_PATH}?${query}`;
}

/** Narrows a raw `direction` query value; anything else falls back to the caller's current tab. */
export function parseActivityDirection(value: string | null): ActivityDirection | null {
  return value === "payins" || value === "payouts" ? value : null;
}

/** Query params of a linked scope, in the order they are written. `status` seeds the toolbar's status filter. */
export const ACTIVITY_SCOPE_PARAMS = {
  organizationId: "organization_id",
  organizationName: "organization_name",
  rail: "rail",
  country: "country",
  currency: "currency",
  sourceCurrency: "source_currency",
  status: "status",
  from: "from",
  to: "to",
  dateField: "date_field",
} as const;

export interface ActivityScopeLink extends Partial<ActivityScope> {
  /** Raw status of the direction's vocabulary (`CONFIRMED` for payins, `PAID` for payouts…). */
  status?: string | null;
}

/** Link to the activity list holding exactly the operations a figure counted. */
export function buildActivityScopePath(direction: ActivityDirection, scope: ActivityScopeLink): string {
  const query = new URLSearchParams({ [ACTIVITY_DIRECTION_PARAM]: direction });
  for (const [key, param] of Object.entries(ACTIVITY_SCOPE_PARAMS)) {
    const value = scope[key as keyof ActivityScopeLink];
    if (typeof value === "string" && value !== "") query.set(param, value);
  }
  return `${PAYMENTS_ACTIVITY_BASE_PATH}?${query}`;
}

/** The scope a link carries, and its status; null scope when the URL carries none of it. */
export function parseActivityScope(params: URLSearchParams): {
  scope: ActivityScope | null;
  status: string | null;
} {
  const read = (key: keyof typeof ACTIVITY_SCOPE_PARAMS) => {
    const value = params.get(ACTIVITY_SCOPE_PARAMS[key])?.trim();
    return value ? value : null;
  };
  const instant = (key: "from" | "to") => {
    const value = read(key);
    return value && !Number.isNaN(Date.parse(value)) ? value : null;
  };
  const scope: ActivityScope = {
    organizationId: read("organizationId"),
    organizationName: read("organizationName"),
    rail: read("rail"),
    country: read("country"),
    currency: read("currency"),
    sourceCurrency: read("sourceCurrency"),
    from: instant("from"),
    to: instant("to"),
    dateField: read("dateField") === "completed" ? "completed" : "created",
  };
  const hasScope =
    scope.organizationId !== null ||
    scope.rail !== null ||
    scope.country !== null ||
    scope.currency !== null ||
    scope.sourceCurrency !== null ||
    scope.from !== null ||
    scope.to !== null;
  return { scope: hasScope ? scope : null, status: read("status") };
}

/** Removes a linked scope from the URL, keeping the direction and the open operation. */
export function withoutActivityScope(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const param of Object.values(ACTIVITY_SCOPE_PARAMS)) next.delete(param);
  return next;
}

/**
 * A status only means something in its direction's vocabulary: `CONFIRMED` is a payin status, `PAID` a payout
 * one, and the other endpoint answers 400 to it. Anything else is dropped rather than sent.
 */
export function statusForDirection(status: string | null, direction: ActivityDirection): string | null {
  return status !== null && STATUS_FILTER_OPTIONS[direction].includes(status) ? status : null;
}

/**
 * The parts of a linked scope a toolbar control takes over. Picking any date option (a preset or "all dates")
 * replaces the link's window; picking any corridor (one or "all") replaces its rail, country and currencies —
 * otherwise the old corridor's rail would keep filtering the new country, and "all" would fall back to it.
 */
export const ACTIVITY_SCOPE_PARTS = {
  window: ["from", "to", "dateField"],
  corridor: ["rail", "country", "currency", "sourceCurrency"],
} as const satisfies Record<string, readonly (keyof typeof ACTIVITY_SCOPE_PARAMS)[]>;

export type ActivityScopePart = keyof typeof ACTIVITY_SCOPE_PARTS;

/**
 * The scope once one of its parts is replaced. Null when nothing is left, so no banner claims a filter the
 * query no longer applies.
 */
export function scopeWithout(scope: ActivityScope, part: ActivityScopePart): ActivityScope | null {
  const rest: ActivityScope =
    part === "window"
      ? { ...scope, from: null, to: null, dateField: "created" }
      : { ...scope, rail: null, country: null, currency: null, sourceCurrency: null };
  const remains =
    rest.organizationId !== null ||
    rest.rail !== null ||
    rest.country !== null ||
    rest.currency !== null ||
    rest.sourceCurrency !== null ||
    rest.from !== null ||
    rest.to !== null;
  return remains ? rest : null;
}

/** Removes the given scope params from the URL, keeping everything else. */
export function withoutActivityParams(
  params: URLSearchParams,
  keys: readonly (keyof typeof ACTIVITY_SCOPE_PARAMS)[],
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of keys) next.delete(ACTIVITY_SCOPE_PARAMS[key]);
  return next;
}
